import { useState } from 'react';
import toast from 'react-hot-toast';
import { useUserFilters } from '../hooks/useUserFilters';
import {
  useUsers,
  useBlockUser,
  useUnblockUser,
  useInvalidateUserTokens,
} from '../services/usersApi';
import { UserListTable } from '../components/UserListTable';
import { ConfirmationModal } from '../components/ConfirmationModal';
import type { UserAction, SortBy } from '../types/user.types';

interface ConfirmationState {
  isOpen: boolean;
  action: UserAction | null;
  userId: string | null;
  userEmail: string | null;
}

interface UsersListViewProps {
  hideHeader?: boolean;
}

export function UsersListView({ hideHeader = false }: UsersListViewProps) {
  const { filters, setSearch, setRole, setStatus, setSortBy, setSortOrder, setPage, resetFilters } =
    useUserFilters();

  const { data: usersData, isLoading, error, refetch } = useUsers(filters);
  const blockUserMutation = useBlockUser();
  const unblockUserMutation = useUnblockUser();
  const invalidateTokensMutation = useInvalidateUserTokens();

  const [confirmationState, setConfirmationState] = useState<ConfirmationState>({
    isOpen: false,
    action: null,
    userId: null,
    userEmail: null,
  });

  // Calculate pagination
  const currentPage = filters.page || 1;
  const limit = filters.limit || 50;
  const totalUsers = usersData?.total || 0;
  const totalPages = Math.ceil(totalUsers / limit);
  const showingFrom = totalUsers > 0 ? (currentPage - 1) * limit + 1 : 0;
  const showingTo = Math.min(currentPage * limit, totalUsers);

  // Handle user actions
  const handleAction = (action: UserAction, userId: string, userEmail: string) => {
    setConfirmationState({
      isOpen: true,
      action,
      userId,
      userEmail,
    });
  };

  const handleConfirm = async () => {
    if (!confirmationState.userId) return;

    try {
      switch (confirmationState.action) {
        case 'block':
          await blockUserMutation.mutateAsync(confirmationState.userId);
          toast.success('User blocked successfully');
          break;
        case 'unblock':
          await unblockUserMutation.mutateAsync(confirmationState.userId);
          toast.success('User unblocked successfully');
          break;
        case 'forceLogout':
          await invalidateTokensMutation.mutateAsync(confirmationState.userId);
          toast.success('User logged out successfully');
          break;
      }
      setConfirmationState({ isOpen: false, action: null, userId: null, userEmail: null });
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message || 'Operation failed';
      toast.error(message);
    }
  };

  const handleCancel = () => {
    setConfirmationState({ isOpen: false, action: null, userId: null, userEmail: null });
  };

  // Get modal content based on action
  const getModalContent = () => {
    const { action, userEmail } = confirmationState;
    switch (action) {
      case 'block':
        return {
          title: 'Block User',
          message: `Are you sure you want to block ${userEmail}?`,
          confirmText: 'Block',
        };
      case 'unblock':
        return {
          title: 'Unblock User',
          message: `Are you sure you want to unblock ${userEmail}?`,
          confirmText: 'Unblock',
        };
      case 'forceLogout':
        return {
          title: 'Force Logout',
          message: `Force logout ${userEmail}? This will invalidate all their tokens.`,
          confirmText: 'Force Logout',
        };
      default:
        return {
          title: 'Confirm Action',
          message: 'Are you sure?',
          confirmText: 'Confirm',
        };
    }
  };

  const modalContent = getModalContent();
  const isActionLoading =
    blockUserMutation.isPending ||
    unblockUserMutation.isPending ||
    invalidateTokensMutation.isPending;

  return (
    <div className="space-y-6">
      {/* Header */}
      {!hideHeader && (
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold text-gray-900">User Management</h1>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div>
            <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
              Search
            </label>
            <input
              type="text"
              id="search"
              placeholder="Email or name (min 3 characters)..."
              value={filters.search || ''}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Role Filter */}
          <div>
            <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">
              Role
            </label>
            <select
              id="role"
              value={filters.role || 'all'}
              onChange={(e) => setRole(e.target.value as 'user' | 'admin' | 'all')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Any</option>
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
          </div>

          {/* Status Filter */}
          <div>
            <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              id="status"
              value={filters.status || 'all'}
              onChange={(e) => setStatus(e.target.value as 'active' | 'blocked' | 'all')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Any</option>
              <option value="active">Active</option>
              <option value="blocked">Blocked</option>
            </select>
          </div>

          {/* Sort */}
          <div>
            <label htmlFor="sort" className="block text-sm font-medium text-gray-700 mb-1">
              Sort By
            </label>
            <div className="flex gap-2">
              <select
                id="sort"
                value={filters.sortBy || 'createdAt'}
                onChange={(e) => setSortBy(e.target.value as SortBy)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="createdAt">Created Date</option>
              </select>
              <button
                type="button"
                onClick={() => setSortOrder(filters.sortOrder === 'asc' ? 'desc' : 'asc')}
                className="px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                title="Toggle sort order"
              >
                {filters.sortOrder === 'asc' ? '↑' : '↓'}
              </button>
            </div>
          </div>
        </div>

        {/* Reset Filters */}
        <div className="flex justify-end">
          <button
            type="button"
            onClick={resetFilters}
            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
          >
            Reset Filters
          </button>
        </div>
      </div>

      {/* Total Count */}
      <div className="flex justify-between items-center">
        <p className="text-sm text-gray-700">
          {totalUsers > 0 ? (
            <>
              Showing <span className="font-semibold">{showingFrom}</span> to{' '}
              <span className="font-semibold">{showingTo}</span> of{' '}
              <span className="font-semibold">{totalUsers}</span> users
            </>
          ) : (
            'No users found'
          )}
        </p>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Error loading users: {error.message || 'Unknown error'}</p>
          <button
            type="button"
            onClick={() => refetch()}
            className="mt-2 text-sm text-red-600 hover:text-red-800 font-medium"
          >
            Retry
          </button>
        </div>
      )}

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow">
        <UserListTable
          users={usersData?.items || []}
          isLoading={isLoading}
          onAction={handleAction}
        />
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-4">
          <button
            type="button"
            onClick={() => setPage(currentPage - 1)}
            disabled={currentPage === 1}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>

          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-700">
              Page {currentPage} of {totalPages}
            </span>
            <input
              type="number"
              min="1"
              max={totalPages}
              value={currentPage}
              onChange={(e) => {
                const page = parseInt(e.target.value, 10);
                if (page >= 1 && page <= totalPages) {
                  setPage(page);
                }
              }}
              className="w-16 px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <button
            type="button"
            onClick={() => setPage(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmationState.isOpen}
        title={modalContent.title}
        message={modalContent.message}
        confirmText={modalContent.confirmText}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        isLoading={isActionLoading}
      />
    </div>
  );
}
